
interface PlanRecommendationProps {
  jobsPerMonth: number;
  onStartTrial: () => void;
}

const plans = [
  {
    id: 'trial',
    name: 'Free Trial',
    price: 0,
    maxJobs: 5,
    description: 'Perfect for testing the waters',
    features: ['5 quotes & invoices', '7-day full access', 'AI cost analysis', 'Mobile app'],
    gradient: 'from-slate-600 to-slate-700',
    badge: '',
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 39,
    maxJobs: 30,
    description: 'For growing trade businesses',
    features: ['Unlimited quotes', '15 team members', 'Supplier pricing', 'Custom branding'],
    gradient: 'from-amber-500 to-orange-600',
    badge: 'RECOMMENDED',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 79,
    maxJobs: 999,
    description: 'For large teams & high volume',
    features: ['Everything in Pro', 'Unlimited team', 'Dedicated manager', 'API access'],
    gradient: 'from-slate-800 to-slate-900',
    badge: '',
  },
];

export default function PlanRecommendation({ jobsPerMonth, onStartTrial }: PlanRecommendationProps) {
  const recommendedPlan =
    jobsPerMonth <= 5 ? 'trial' : jobsPerMonth <= 30 ? 'professional' : 'enterprise';

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
          <i className="ri-star-line text-amber-600"></i>
        </div>
        <h3 className="text-lg font-bold text-slate-900">Recommended Plan for You</h3>
      </div>

      <div className="grid gap-4">
        {plans.map((plan) => {
          const isRecommended = plan.id === recommendedPlan;
          return (
            <div
              key={plan.id}
              className={`relative rounded-xl p-5 border-2 transition-all duration-300 cursor-pointer ${
                isRecommended
                  ? 'border-amber-400 bg-amber-50/50 shadow-lg shadow-amber-100'
                  : 'border-slate-100 bg-white hover:border-slate-200'
              }`}
            >
              {isRecommended && plan.badge && (
                <div className="absolute -top-3 right-4 px-3 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold rounded-full tracking-wider">
                  {plan.badge}
                </div>
              )}

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 bg-gradient-to-br ${plan.gradient} rounded-lg flex items-center justify-center`}
                  >
                    <i className="ri-tools-fill text-white text-lg"></i>
                  </div>
                  <div>
                    <h4 className={`font-bold text-sm ${isRecommended ? 'text-amber-700' : 'text-slate-800'}`}>
                      {plan.name}
                    </h4>
                    <p className="text-xs text-slate-500">{plan.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-xl font-bold ${isRecommended ? 'text-amber-600' : 'text-slate-800'}`}>
                    ${plan.price}
                  </p>
                  <p className="text-[10px] text-slate-400">/month (yearly)</p>
                </div>
              </div>

              {isRecommended && (
                <div className="mt-4 pt-4 border-t border-amber-200">
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {plan.features.map((f, i) => (
                      <div key={i} className="flex items-center gap-1.5">
                        <i className="ri-check-line text-emerald-500 text-xs"></i>
                        <span className="text-xs text-slate-600">{f}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={onStartTrial}
                    className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-lg hover:from-amber-600 hover:to-orange-700 transition-all shadow-md hover:shadow-lg cursor-pointer whitespace-nowrap text-sm"
                  >
                    Start 7-Day Free Trial
                    <i className="ri-arrow-right-line ml-2"></i>
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
